import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import Footer from '../components/Footer'
import PriceSwitch, { BillingCycle, PlanTierId } from '../components/PriceSwitch'
import { AnimatedBot } from '../components/AnimateIcon'
import {
  LucideIcon,
  Check,
  ArrowRight,
  ChevronDown,
  Store,
  Minus,
  Terminal,
  Cpu,
  ShieldCheck,
} from 'lucide-react'

interface PricingTier {
  id: PlanTierId
  name: string
  monthlyPrice: string
  annualPrice: string
  period?: string
  badge?: string
  description: string
  highlighted?: boolean
  includesPrevious?: string
  features: string[]
  ctaText: string
  ctaLink: string
  ctaExternal?: boolean
}

const pricingTiers: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    monthlyPrice: '$0',
    annualPrice: '$0',
    period: '/ forever',
    badge: 'Open Source',
    description: 'For developers and hackers prototyping, experimenting, and learning Solarch.',
    features: [
      'Solarch CLI & local runtime',
      'Basic Admin Dashboard',
      'Limited active projects',
      'Basic Project Doctor diagnostics',
      'Basic SDK access',
      'Basic authentication & SQLite database',
      'Extensible plugin framework',
      'BYOK (Bring Your Own Key) integrations',
    ],
    ctaText: 'Get Started Free',
    ctaLink: '/docs/overview/getting-started',
  },
  {
    id: 'developer',
    name: 'Developer',
    monthlyPrice: '$15',
    annualPrice: '$12',
    period: '/ month',
    badge: 'Individual',
    includesPrevious: 'All features in Free, plus:',
    description: 'For individual builders moving beyond experimentation into real production apps.',
    features: [
      'AI Agents SDK included',
      'Up to 3 agents / application',
      'Agent persistent memory & execution tools',
      'Full SDK ecosystem (Web, Mobile, Desktop)',
      'Expanded active projects',
      'Automated background sync',
      'Advanced Project Doctor & management',
      'Multi-step workflow engine',
      'Enhanced deployment tooling',
    ],
    ctaText: 'Start Building',
    ctaLink: '/waitlist',
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: '$39',
    annualPrice: '$31',
    period: '/ month',
    badge: 'Most Popular',
    highlighted: true,
    includesPrevious: 'All features in Developer, plus:',
    description: 'For serious builders and fast-moving teams building complex multi-agent applications.',
    features: [
      'Multi-agent orchestration engine',
      'Up to 15 agents / application',
      'Agent delegation & task hierarchy',
      'Agent-to-agent communication bus',
      'Advanced multi-session agent memory',
      'Advanced conditional workflows',
      'Advanced Model Context Protocol (MCP)',
      'Unlimited active projects',
      'Team collaboration & shared workspaces',
      'Audit logs & advanced security policies',
    ],
    ctaText: 'Start Building',
    ctaLink: '/waitlist',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    monthlyPrice: 'Custom',
    annualPrice: 'Custom',
    badge: 'Organization',
    includesPrevious: 'All features in Pro, plus:',
    description: 'For organizations requiring custom infrastructure, compliance, and dedicated support.',
    features: [
      'SSO & SAML authentication',
      'Enterprise fine-grained RBAC',
      'Dedicated 24/7 support & response SLAs',
      'Custom agent runtime & sandboxing',
      'Private Cloud & dedicated infrastructure',
      'Compliance support & SOC2 readiness',
      'Custom architecture & deployment review',
    ],
    ctaText: 'Contact Sales',
    ctaLink: '/feedback',
  },
]

interface ComparisonCategory {
  category: string
  description: string
  icon: LucideIcon | typeof AnimatedBot
  features: {
    name: string
    free: string | boolean
    developer: string | boolean
    pro: string | boolean
    enterprise: string | boolean
  }[]
}

const comparisonData: ComparisonCategory[] = [
  {
    category: 'Core Platform',
    description: 'Foundational CLI, database, dashboard, and extensible plugin architecture',
    icon: Terminal,
    features: [
      { name: 'Solarch CLI', free: 'Basic CLI', developer: 'Full CLI', pro: 'Full CLI', enterprise: 'Custom CLI' },
      { name: 'Admin Dashboard', free: 'Basic', developer: 'Advanced', pro: 'Advanced', enterprise: 'Custom & White-Label' },
      { name: 'Active Projects', free: 'Limited', developer: 'Expanded', pro: 'Unlimited', enterprise: 'Unlimited' },
      { name: 'Auth & Database Capabilities', free: 'Basic', developer: 'Full', pro: 'Full', enterprise: 'Enterprise' },
      { name: 'Plugin Framework', free: true, developer: true, pro: true, enterprise: 'Custom Plugins' },
      { name: 'BYOK Integrations', free: true, developer: true, pro: true, enterprise: 'Dedicated Key Management' },
    ],
  },
  {
    category: 'SDK & Development',
    description: 'Client libraries, cross-platform targets, background sync, and diagnostics',
    icon: Cpu,
    features: [
      { name: 'SDK Access', free: 'Basic Access', developer: 'Full Ecosystem', pro: 'Advanced Capabilities', enterprise: 'Custom Extensions' },
      { name: 'Desktop & Mobile Platforms', free: false, developer: true, pro: true, enterprise: true },
      { name: 'Background Sync', free: false, developer: true, pro: true, enterprise: true },
      { name: 'Project Doctor Diagnostics', free: 'Basic', developer: 'Advanced', pro: 'Advanced', enterprise: 'Enterprise' },
      { name: 'Deployment Tooling', free: 'Community', developer: 'Enhanced', pro: 'Advanced', enterprise: 'Dedicated Infra' },
    ],
  },
  {
    category: 'AI & Agents',
    description: 'Multi-agent orchestration, agent memory, execution tools, and workflows',
    icon: AnimatedBot,
    features: [
      { name: 'AI Agents SDK', free: false, developer: true, pro: true, enterprise: true },
      { name: 'Max Agents / Application', free: '—', developer: 'Up to 3 agents', pro: 'Up to 15 agents', enterprise: 'Custom / Unlimited' },
      { name: 'Agent Memory', free: false, developer: 'Standard Memory', pro: 'Advanced Memory', enterprise: 'Custom Storage Backend' },
      { name: 'Agent Tools & Execution', free: false, developer: true, pro: true, enterprise: 'Custom Sandboxed Tools' },
      { name: 'Workflow Engine', free: false, developer: 'Basic Workflows', pro: 'Advanced Workflows', enterprise: 'Custom Orchestration' },
      { name: 'Multi-Agent Orchestration', free: false, developer: false, pro: true, enterprise: true },
      { name: 'Agent Delegation & Hierarchy', free: false, developer: false, pro: true, enterprise: true },
      { name: 'Agent-to-Agent Communication', free: false, developer: false, pro: true, enterprise: true },
      { name: 'Model Context Protocol (MCP)', free: false, developer: 'Standard MCP', pro: 'Advanced MCP', enterprise: 'Custom Server MCP' },
    ],
  },
  {
    category: 'Collaboration & Security',
    description: 'Team workspaces, access governance, SSO/SAML, compliance, and private infrastructure',
    icon: ShieldCheck,
    features: [
      { name: 'Team Collaboration', free: false, developer: false, pro: true, enterprise: true },
      { name: 'Audit Logs & Activity History', free: false, developer: false, pro: true, enterprise: true },
      { name: 'Advanced Security Controls', free: false, developer: false, pro: true, enterprise: true },
      { name: 'SSO & SAML Support', free: false, developer: false, pro: false, enterprise: true },
      { name: 'Enterprise RBAC Policies', free: false, developer: false, pro: false, enterprise: true },
      { name: 'Compliance Support', free: false, developer: false, pro: false, enterprise: true },
      { name: 'Dedicated Infrastructure', free: false, developer: false, pro: false, enterprise: true },
    ],
  },
]

const faqs = [
  {
    q: 'Is the Free tier really $0 forever?',
    a: 'Yes. The Free tier is designed for learning, experimenting, and prototyping with the Solarch CLI, basic dashboard, and BYOK AI integrations with no credit card required.',
  },
  {
    q: 'What is included in the Developer tier ($15/mo)?',
    a: 'The Developer tier is built for individual developers taking projects into production. It includes more projects, the full SDK ecosystem (Web, Mobile, Desktop), background sync, advanced Project Doctor, and the AI Agents SDK with up to 3 agents per application, persistent memory, and basic workflows.',
  },
  {
    q: 'What is the difference between Developer and Pro ($39/mo)?',
    a: 'While Developer supports individual apps with up to 3 agents, Pro unlocks multi-agent orchestration with up to 15 agents per application, advanced multi-session memory, agent delegation, agent-to-agent communication, advanced MCP, unlimited projects, and team collaboration.',
  },
  {
    q: 'How do the agent limits work per application?',
    a: 'On the Developer plan, each application can run up to 3 simultaneous agents with independent memory and tools. On the Pro plan, you can coordinate up to 15 agents per application with hierarchical task delegation and direct inter-agent messaging.',
  },
  {
    q: 'Is there usage-based billing or per-request charges?',
    a: 'No. Solarch Phase 1 uses transparent, capability-based subscription tiers. You bring your own model provider API keys (BYOK) for AI execution without surprise usage markups, compute meters, or per-request charges.',
  },
  {
    q: 'What is included in the Enterprise tier?',
    a: 'Enterprise is designed for organizations requiring SSO/SAML, enterprise RBAC, compliance assistance, custom agent runtime sandboxes, private cloud or dedicated infrastructure, and guaranteed response SLAs.',
  },
  {
    q: 'When will the Plugin & Agent Marketplace launch?',
    a: 'The marketplace is on our future ecosystem roadmap. It will allow developers to publish and discover custom agent skills, plugins, and workflow recipes with a 20% platform revenue share.',
  },
]

function FaqItem({ faq, index, isLast }: { faq: typeof faqs[0]; index: number; isLast: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <motion.div
      initial={{ y: 12, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.3, delay: index * 0.04 }}
      className={isLast ? '' : 'border-b border-[#3a2214]'}
    >
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between py-5 text-left text-base font-medium text-theme transition-colors hover:text-primary gap-4 group cursor-pointer"
      >
        <span className="group-hover:text-primary transition-colors">{faq.q}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-theme-muted transition-transform duration-300 ${
            open ? 'rotate-180 text-primary' : 'group-hover:text-primary'
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${
          open ? 'max-h-96 pb-5' : 'max-h-0'
        }`}
      >
        <p className="text-sm text-theme-secondary leading-relaxed">
          {faq.a}
        </p>
      </div>
    </motion.div>
  )
}

function RenderValue({ val, isPro }: { val: string | boolean; isPro?: boolean }) {
  if (typeof val === 'boolean') {
    return val ? (
      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/20 text-primary border border-primary/40 mx-auto shadow-sm">
        <Check size={12} strokeWidth={3} />
      </div>
    ) : (
      <Minus size={14} className="text-theme-muted/30 mx-auto" />
    )
  }
  if (val === '—' || val === '-') {
    return <Minus size={14} className="text-theme-muted/30 mx-auto" />
  }

  return (
    <span
      className={`text-xs sm:text-sm font-medium leading-snug ${
        isPro ? 'text-white font-semibold' : 'text-[#fdf3ec]'
      }`}
    >
      {val}
    </span>
  )
}

export default function Pricing() {
  const [activePlan, setActivePlan] = useState<PlanTierId>('pro')
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('annual')

  const currentTier = pricingTiers.find((t) => t.id === activePlan) || pricingTiers[0]
  const price = billingCycle === 'annual' ? currentTier.annualPrice : currentTier.monthlyPrice

  return (
    <>
      <SEO
        title="Pricing — Solarch"
        description="Simple pricing, powerful infrastructure. Start free for prototyping, and upgrade to Developer, Pro, or Enterprise as your projects and multi-agent requirements grow."
        canonical="/pricing"
      />

      <div className="min-h-screen bg-theme-body pt-14 flex flex-col">
        
        {/* Header / Hero */}
        <section className="relative pt-10 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10 text-center">
          <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="max-w-3xl mx-auto"
            >
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-theme mb-4">
                Simple Pricing. <span className="text-primary">Powerful Infrastructure.</span>
              </h1>

              <p className="text-base sm:text-lg text-theme-secondary leading-relaxed max-w-2xl mx-auto mb-8">
                Start free for prototyping and experimentation. Upgrade as your applications, developer tools, and multi-agent workflows scale.
              </p>

              {/* Interactive Segmented Switcher for all 4 plans & Monthly/Annual */}
              <div className="flex justify-center">
                <PriceSwitch
                  activePlan={activePlan}
                  onPlanChange={setActivePlan}
                  billingCycle={billingCycle}
                  onBillingChange={setBillingCycle}
                />
              </div>
            </motion.div>
          </div>
        </section>

        {/* Single Focused Pricing Card */}
        <section className="py-6 sm:py-8 lg:py-10">
          <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8">
            <div
              className={`rounded-3xl bg-[#150d08] p-6 sm:p-9 relative shadow-2xl transition-all duration-300 ${
                currentTier.highlighted
                  ? 'border-2 border-primary/80 shadow-[0_0_50px_rgba(255,90,31,0.12)]'
                  : 'border border-[#3a2214]'
              }`}
            >
              {/* Badge on Top-Right Border */}
              {currentTier.badge && (
                <div className="absolute -top-3.5 right-6 sm:right-8 z-20">
                  <span
                    className={`inline-flex items-center px-4 py-1 rounded-full text-xs font-semibold shadow-lg ${
                      currentTier.highlighted
                        ? 'bg-white text-black font-bold'
                        : 'bg-[#fdf3ec] text-[#0a0603] font-semibold border border-[#3a2214]'
                    }`}
                  >
                    {currentTier.badge}
                  </span>
                </div>
              )}

              {currentTier.highlighted && (
                <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-transparent to-transparent pointer-events-none rounded-3xl" />
              )}

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTier.id + billingCycle}
                  initial={{ opacity: 0, y: 8, scale: 0.99 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.99 }}
                  transition={{ duration: 0.2 }}
                  className="relative z-10 flex flex-col"
                >
                  {/* Top Bar: Plan Label on Left */}
                  <div className="flex items-center justify-start mb-2">
                    <span className="font-heading text-sm sm:text-base font-bold text-theme uppercase tracking-wider">
                      {currentTier.name} Plan
                    </span>
                  </div>

                  {/* Price */}
                  <div className="my-1.5 flex items-baseline gap-2">
                    <span className="font-heading text-5xl sm:text-6xl font-bold text-theme tracking-tight">
                      {price}
                    </span>
                    {currentTier.period && (
                      <span className="text-sm sm:text-base text-theme-secondary font-mono">
                        {billingCycle === 'annual' && currentTier.id !== 'free' && currentTier.id !== 'enterprise'
                          ? '/ month (billed annually)'
                          : currentTier.period}
                      </span>
                    )}
                  </div>

                  {/* Tagline Description */}
                  <p className="text-sm sm:text-base text-theme-secondary leading-relaxed mb-6">
                    {currentTier.description}
                  </p>

                  {/* Prominent Action Button */}
                  <Link
                    to={currentTier.ctaLink}
                    className={`w-full inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl font-semibold text-sm sm:text-base transition-all active:scale-[0.98] ${
                      currentTier.highlighted
                        ? 'bg-primary text-white shadow-xl shadow-primary/30 hover:brightness-110'
                        : 'border border-[#3a2214] bg-[#0a0603] text-theme hover:bg-[#1f140c] hover:border-primary/50'
                    }`}
                  >
                    <span>{currentTier.ctaText}</span>
                    <ArrowRight size={16} />
                  </Link>

                  <div className="h-px bg-[#3a2214] my-6" />

                  {/* 2-Column Feature Checklist */}
                  <div>
                    <h3 className="text-xs font-mono font-semibold uppercase tracking-wider mb-3.5">
                      {currentTier.includesPrevious ? (
                        <span className="text-primary font-bold">
                          {currentTier.includesPrevious}
                        </span>
                      ) : (
                        <span className="text-theme-muted">
                          Included in {currentTier.name}:
                        </span>
                      )}
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3.5 text-xs sm:text-sm text-[#fdf3ec]">
                      {currentTier.features.map((feature, fIdx) => (
                        <li key={fIdx} className="flex items-start gap-2.5 group">
                          <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary border border-primary/40 group-hover:bg-primary group-hover:text-white transition-colors">
                            <Check size={11} strokeWidth={3} />
                          </div>
                          <span className="leading-snug text-theme-secondary font-medium group-hover:text-theme transition-colors">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </section>

        {/* Feature Comparison Tables Broken by Category */}
        <section className="pt-20 pb-16 sm:pt-28 sm:pb-24 relative">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-theme tracking-tight mb-3">
                Feature Comparison
              </h2>
              <p className="text-sm sm:text-base text-theme-secondary">
                Detailed capability breakdown across Free, Developer, Pro, and Enterprise tiers.
              </p>
            </div>

            <div className="space-y-8 sm:space-y-10">
              {comparisonData.map((cat, cIdx) => {
                const IconComponent = cat.icon
                return (
                  <div
                    key={cIdx}
                    className="rounded-2xl border border-[#3a2214] bg-[#150d08] shadow-2xl overflow-hidden"
                  >
                    {/* Category Header Bar */}
                    <div className="px-5 sm:px-6 py-4 bg-[#0e0805] border-b border-[#3a2214] flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10 border border-primary/25 text-primary">
                          <IconComponent size={20} />
                        </div>
                        <div>
                          <h3 className="font-heading text-base sm:text-lg font-bold text-theme">
                            {cat.category}
                          </h3>
                          <p className="text-xs text-theme-secondary">
                            {cat.description}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Table for this Category */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs sm:text-sm border-collapse min-w-[720px]">
                        <thead>
                          <tr className="border-b border-[#3a2214] bg-[#0a0603]/60">
                            <th className="p-4 sm:px-6 sm:py-3.5 font-heading text-xs font-semibold uppercase tracking-wider text-theme-muted w-2/5">
                              Feature
                            </th>
                            <th className="p-4 sm:px-4 sm:py-3.5 text-center font-heading text-xs font-semibold uppercase tracking-wider text-theme-muted w-[15%]">
                              Free ($0)
                            </th>
                            <th className="p-4 sm:px-4 sm:py-3.5 text-center font-heading text-xs font-semibold uppercase tracking-wider text-theme-muted w-[15%]">
                              Developer ($15)
                            </th>
                            <th className="p-4 sm:px-4 sm:py-3.5 text-center font-heading text-xs font-semibold uppercase tracking-wider text-primary w-[15%] bg-primary/[0.08] border-x border-primary/30">
                              Pro ($39)
                            </th>
                            <th className="p-4 sm:px-4 sm:py-3.5 text-center font-heading text-xs font-semibold uppercase tracking-wider text-theme-muted w-[15%]">
                              Enterprise
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {cat.features.map((feat, fIdx) => (
                            <tr
                              key={fIdx}
                              className="border-b border-[#3a2214]/60 last:border-0 hover:bg-[#1a110a] transition-colors"
                            >
                              <td className="p-4 sm:px-6 sm:py-3.5 font-medium text-theme">
                                {feat.name}
                              </td>
                              <td className="p-4 sm:px-4 sm:py-3.5 text-center">
                                <RenderValue val={feat.free} />
                              </td>
                              <td className="p-4 sm:px-4 sm:py-3.5 text-center">
                                <RenderValue val={feat.developer} />
                              </td>
                              <td className="p-4 sm:px-4 sm:py-3.5 text-center bg-primary/[0.03] border-x border-primary/20">
                                <RenderValue val={feat.pro} isPro />
                              </td>
                              <td className="p-4 sm:px-4 sm:py-3.5 text-center">
                                <RenderValue val={feat.enterprise} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Future Ecosystem: Plugin & Agent Marketplace */}
        <section className="py-12">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl border border-[#3a2214] bg-[#150d08] p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 shadow-xl">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-[#0a0603] border border-[#3a2214] text-primary shrink-0 mt-1">
                  <Store size={22} />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-theme mb-1.5">
                    Future Ecosystem: Plugin &amp; Agent Marketplace
                  </h3>
                  <p className="text-xs sm:text-sm text-theme-secondary leading-relaxed">
                    A community and commercial marketplace allowing developers to publish, monetize, and distribute custom agent skills, workflow recipes, and integrations with a 20% platform revenue share.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Frequently Asked Questions */}
        <section className="py-16">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-theme mb-3">
                Frequently Asked Questions
              </h2>
              <p className="text-sm text-theme-secondary">
                Common questions about Solarch tiers, agent limits, and subscription capabilities.
              </p>
            </div>

            <div className="rounded-2xl border border-[#3a2214] bg-[#150d08] px-6 sm:px-8 shadow-xl backdrop-blur-sm">
              {faqs.map((faq, idx) => (
                <FaqItem key={idx} faq={faq} index={idx} isLast={idx === faqs.length - 1} />
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}
